import { useMutation } from '@apollo/client';
import { useState } from 'react';
import cn from 'classnames';
import { Modal, ModalProps } from 'react-responsive-modal';
// Api
import { CREATE_UNREGISTERED_CONTACT } from 'api/mentions/mutations';
import {
  GET_MEDIA_POSTS,
  GET_VIDEO_REQUESTS_FOR_STORE,
} from 'api/mediaPost/queries';
import { ACCEPT_VIDEO_EDIT_SUBMISSIONS } from 'api/mediaPost/mutations';
// Types
import {
  CreateUnregisteredContact,
  CreateUnregisteredContactVariables,
} from 'api/mentions/types/CreateUnregisteredContact';
import { GetVideoEditRequestsForStore_getVideoEditRequestsForStore_entities } from 'api/mediaPost/types/GetVideoEditRequestsForStore';
import {
  MediaPostStatus,
  StoreStatus,
  VideoEditRequestSubmissionStatus,
} from 'api/graphql-global-types';
import {
  AcceptVideoEditSubmissions,
  AcceptVideoEditSubmissionsVariables,
} from 'api/mediaPost/types/AcceptVideoEditSubmissions';
// Helpers
import { formatMentionsInput } from 'helpers/mentions';
// Components
import SingleVideoCard from './components/SingleVideoCard/SingleVideoCard';
import VideoFeedbackModal from './components/VideoFeedbackModal/VideoFeedbackModal';
import { showToast } from 'components/common/Toast/Toast';
// Ui3
import Button from 'ui3/Button/Button';
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
// Hooks
import { useBreakpoints } from 'hooks';
// Styles
import 'react-responsive-modal/styles.css';
import styles from './ReviewVideoRequestModal.module.scss';

type ReviewVideoRequestModalProps = {
  storeId: string;
  isOpen: boolean;
  onClose: () => void;
  videoRequest: GetVideoEditRequestsForStore_getVideoEditRequestsForStore_entities;
  /** Optional class for overlay and container (e.g. dashboard smart-banner padding). */
  modalAreaClassName?: string;
  /** Optional class applied to the modal div (e.g. for dashboard smart-banner margin). */
  modalClassName?: string;
};

const ReviewVideoRequestModal = ({
  storeId,
  isOpen,
  onClose,
  videoRequest,
  modalAreaClassName,
  modalClassName,
}: ReviewVideoRequestModalProps): JSX.Element | null => {
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState<boolean>(
    false
  );

  const [editedVideos, setEditedVideos] = useState(videoRequest.editedVideos);

  // check if any of the videos was rejected
  const rejectedVideos = videoRequest.editedVideos.filter(
    (video) =>
      video.reviewSubmissions?.[0]?.status ===
      VideoEditRequestSubmissionStatus.Rejected
  );

  // show approve all button if there are multiple videos and none of them are rejected
  const initialShowApproveAllButton =
    videoRequest.editedVideos.length > 1 && !rejectedVideos.length;

  const [showApproveAllButton, setShowApproveAllButton] = useState<boolean>(
    initialShowApproveAllButton
  );

  const [editViewOpenCount, setEditViewOpenCount] = useState<number>(0);
  const { isBreakpointL } = useBreakpoints();

  const [acceptVideoEditSubmissions, { loading }] = useMutation<
    AcceptVideoEditSubmissions,
    AcceptVideoEditSubmissionsVariables
  >(ACCEPT_VIDEO_EDIT_SUBMISSIONS);

  const [createUnregisteredContact] = useMutation<
    CreateUnregisteredContact,
    CreateUnregisteredContactVariables
  >(CREATE_UNREGISTERED_CONTACT);

  const refetchGetVideosQueryInput = {
    query: GET_VIDEO_REQUESTS_FOR_STORE,
    variables: {
      input: {
        storeId: storeId,
      },
    },
  };

  const refetchGetMediaFeedQueryInput = {
    query: GET_MEDIA_POSTS,
    variables: {
      input: {
        storeId,
        storeStatuses: [StoreStatus.Active, StoreStatus.Pending],
        statuses: [
          MediaPostStatus.published,
          MediaPostStatus.scheduled,
          MediaPostStatus.scheduledInstant,
        ],
      },
    },
  };

  const formatMentionsForEditedVideo = async (mentionsValues) => {
    const createUnregisteredContactInput =
      mentionsValues?.filter((mention) => !mention.targetType) || [];

    const unregisterContactsData = await createUnregisteredContact({
      variables: {
        input: createUnregisteredContactInput,
      },
    });

    const newUnregisteredContacts =
      unregisterContactsData.data?.createUnregisteredContact?.map(
        (contact) => ({
          unregisteredContactId: contact.id,
        })
      ) || [];

    const oldMentions =
      mentionsValues?.filter((mention) => mention?.targetType) || [];
    const formatedOldMentions = formatMentionsInput(oldMentions);

    return [...formatedOldMentions, ...newUnregisteredContacts];
  };

  const formatHashtagsInput = (hashtags) => {
    if (hashtags?.length) {
      return hashtags.map((hashtag) => {
        // Check if hashtag is an object with a `name` property
        if (typeof hashtag === 'object' && 'name' in hashtag) {
          return { name: hashtag.name };
        }
        // Otherwise map it directly
        return { name: hashtag };
      });
    } else {
      return null;
    }
  };

  const handleApproveAll = async (feedback: string, rating: number) => {
    const submissionsInput = await Promise.all(
      editedVideos?.map(async (editedVideo) => ({
        id: editedVideo.reviewSubmissions[0].id || '',
        hashtagInputs: formatHashtagsInput(editedVideo.hashtags),
        mentionsInputs: await formatMentionsForEditedVideo(
          editedVideo.mentions
        ),
      })) || []
    );

    try {
      acceptVideoEditSubmissions({
        variables: {
          input: {
            feedback: feedback,
            rate: rating,
            requestId: videoRequest.id,
            submissions: submissionsInput,
          },
        },
        refetchQueries: [
          refetchGetVideosQueryInput,
          refetchGetMediaFeedQueryInput,
        ],
      });
      showToast({
        message: `You've successfully accepted all the videos for this review.`,
        type: 'success',
      });
      handleCloseFeedbackModal();
      onClose();
    } catch (error) {
      showToast({
        message: error?.message,
        type: 'error',
      });
    }
  };

  const handleOpenFeedbackModal = () => {
    setIsFeedbackModalOpen(true);
  };

  const handleCloseFeedbackModal = () => {
    setIsFeedbackModalOpen(false);
  };

  const handleVideoRejected = () => {
    setShowApproveAllButton(false);
  };

  const handleChange = (editedVideoItem) => {
    const updatedEditedVideos = editedVideos.map((video) =>
      video.id === editedVideoItem.id ? editedVideoItem : video
    );
    setEditedVideos(updatedEditedVideos);
  };

  const hasMultipleItems = videoRequest.editedVideos.length > 1;

  const handleEditViewChange = (isOpen: boolean) => {
    setEditViewOpenCount((prev) => (isOpen ? prev + 1 : Math.max(0, prev - 1)));
  };

  const classNames: ModalProps['classNames'] = {
    overlay: cn(styles.overlay, modalAreaClassName),
    modalContainer: modalAreaClassName,
    modal: cn(
      styles.modal,
      styles.root,
      {
        [styles.hasMultipleItems]: hasMultipleItems,
        [styles['size-medium']]: !hasMultipleItems && editViewOpenCount === 0,
        [styles['size-large']]: hasMultipleItems || editViewOpenCount > 0,
      },
      modalClassName
    ),
    closeButton: styles.closeButton,
    closeIcon: styles.closeIcon,
  };

  return (
    <>
      <Modal
        open={isOpen}
        onClose={onClose}
        center
        classNames={classNames}
        showCloseIcon={true}
        closeIcon={<MillionsIcon name="close" className={styles.closeIcon} />}
        blockScroll={isBreakpointL}
        focusTrapped={true}
        closeOnOverlayClick={true}
      >
        <div className={styles.wrapper}>
          <div className={styles.headerSection}>
            <div id="modal-title" className={styles.title}>
              Videos for review:
            </div>
            <div className={styles.subtitle}>
              After you approve these videos they will appear on your media
              feed.
            </div>
          </div>
          <div className={styles.videoRequestsWrapper}>
            {videoRequest.editedVideos.map((videoItem, index) => (
              <SingleVideoCard
                key={videoItem.id}
                videoItem={videoItem}
                storeId={storeId}
                onRejected={handleVideoRejected}
                onChange={handleChange}
                lastReview={videoRequest.editedVideos.length === 1}
                handleCloseAll={onClose}
                clipIndex={index + 1}
                onEditViewChange={handleEditViewChange}
              />
            ))}
          </div>
          {showApproveAllButton && (
            <>
              <Button
                className={styles.approveAllButton}
                variant="success"
                onClick={handleOpenFeedbackModal}
                loading={loading}
              >
                Approve all
              </Button>
              <p className={styles.footer}>
                By approving all videos, all the videos will be published on
                your media feed.
              </p>
            </>
          )}
        </div>
      </Modal>

      <VideoFeedbackModal
        isOpen={isFeedbackModalOpen}
        isApprove={true}
        isMultiple
        onClose={handleCloseFeedbackModal}
        sendFeedback={handleApproveAll}
      />
    </>
  );
};

export default ReviewVideoRequestModal;
